import React from 'react';
import {BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useSearchParams} from 'react-router-dom';
import GA4PageViews from "../analytics/GA4PageViews";
import {useSelector} from 'react-redux';
import {RootState} from '../store/rootReducer';
import RequiredRole from "./requiredRole";

import OverviewPage from '../pages/Overview/page';
import AdvertiserGuidePage from '../pages/AdvertiserGuide/page';
import PartnerGuidePage from '../pages/PartnerGuide/page';
import PromoterGuidePage from '../pages/PromoterGuide/page';
import SetPasswordPage from '../pages/VerifyAccount/page';
import LoadingPage from '../pages/Click/loading/page';
import OnboardingRefreshPage from '../pages/Profile/OnboardingRefresh/page';
import OnboardingSuccessPage from '../pages/Profile/OnboardingSuccess/page';
import UpgradeSuccessPage from '../pages/Profile/UpgradeSuccess/page';
import Home from '../pages/Home/page';
import WalletPage from '../pages/Wallet/walletDetails/page';
import ProfilePage from '../pages/Profile/page';
import PrivacyPolicyPage from '../pages/FooterPages/PrivacyPolicy/page';
import TermsAndConditionsPage from '../pages/FooterPages/TermsAndConditions/page';
import AboutPage from "../pages/FooterPages/About/page";
import PricingPage from "../pages/FooterPages/Pricing/page";
import CareersPage from "../pages/FooterPages/Careers/page";
import ContactPage from "../pages/FooterPages/Contact/page";
import RefundsPage from "../pages/FooterPages/Refunds/page";
import CancellationsPage from "../pages/FooterPages/Cancellations/page";

import CampaignDetailPage from '../pages/Campaign/campaign/page';
import UpdateCampaignPage from '../pages/Campaign/updateCampaign/page';
import UpdateActiveCampaignPage from '../pages/Campaign/updateActiveCampaign/page';
import CreateCampaignPage from '../pages/Campaign/createCampaign/page';
import CampaignListPage from '../pages/Campaign/campaignList/page';
import CampaignLinkListPage from '../pages/Campaign/campaignLinkList/page';
import CampaignLinkDetailPage from '../pages/Campaign/campaignLink/page';

import TransactionsPage from '../pages/Wallet/Transactions/page';

import BestAdvertisersPage from '../pages/Advertiser/Best/page';
import AdvertiserPage from '../pages/Advertiser/page';
import BestPromotersPage from '../pages/Promoter/Best/page';
import PromoterPage from '../pages/Promoter/page';
import BestPartnersPage from '../pages/Partner/Best/page';
import PartnerPage from '../pages/Partner/page';

import InviteUsersPage from '../pages/Invite/page';

import CampaignPromotersPage from "../pages/Campaign/campaignPromoters/page";

import CampaignLinkRequestsPage from "../pages/Campaign/campaignLinkRequest/page";
import CampaignLinkRequestDetailPage from "../pages/Campaign/campaignLinkRequest/Detail/page";
import CampaignRequestApprovePage from "../pages/Campaign/campaignLinkRequest/Approve/page";
import CampaignRequestRejectPage from "../pages/Campaign/campaignLinkRequest/Reject/page";

import MainLayout from '../components/molecules/MainLayout/component';

const RequireAuth: React.FC<{ children: JSX.Element }> = ({children}) => {
    const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
    return isAuthenticated ? children : <Navigate to="/" replace/>;
};

const NotAuthenticatedRoute: React.FC<{ children: JSX.Element }> = ({children}) => {
    const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
    return !isAuthenticated ? children : <Navigate to="/home" replace/>;
};

const ClickRedirectWrapper: React.FC = () => {
    const {hash} = useParams<{ hash: string }>();
    const [searchParams] = useSearchParams();
    const target = searchParams.get('target') || '';
    return <LoadingPage hash={hash || ''} target={target}/>;
};

const AppRouter: React.FC = () => (
    <BrowserRouter>
        <GA4PageViews/>
        <Routes>
            {/* Public / landing routes */}
            <Route path="/" element={<NotAuthenticatedRoute><OverviewPage/></NotAuthenticatedRoute>}/>
            <Route path="signup" element={<NotAuthenticatedRoute><OverviewPage/></NotAuthenticatedRoute>}/>
            <Route path="advertiser-guide" element={<AdvertiserGuidePage/>}/>
            <Route path="promoter-guide" element={<PromoterGuidePage/>}/>
            <Route path="partner-guide" element={<PartnerGuidePage/>}/>
            <Route path="verifyAccount" element={<SetPasswordPage/>}/>
            <Route path="click/:hash" element={<ClickRedirectWrapper/>}/>
            <Route path="about" element={<AboutPage/>}/>
            <Route path="pricing" element={<PricingPage/>}/>
            <Route path="careers" element={<CareersPage/>}/>
            <Route path="contact" element={<ContactPage/>}/>
            <Route path="privacy-policy" element={<PrivacyPolicyPage/>}/>
            <Route path="terms-and-conditions" element={<TermsAndConditionsPage/>}/>
            <Route path="refunds" element={<RefundsPage/>}/>
            <Route path="cancellations" element={<CancellationsPage/>}/>

            {/* Protected routes all share the same layout */}
            <Route element={<RequireAuth><MainLayout><Outlet/></MainLayout></RequireAuth>}>
                <Route path="home" element={<Home/>}/>
                <Route path="profile" element={<ProfilePage/>}/>
                <Route path="onboard/refresh" element={<OnboardingRefreshPage/>}/>
                <Route path="onboard/success" element={<OnboardingSuccessPage/>}/>
                <Route path="upgrade/success" element={<UpgradeSuccessPage/>}/>

                <Route path="campaigns">
                    <Route path="create" element={
                        <RequiredRole allowed={['advertiser']}>
                            <CreateCampaignPage/>
                        </RequiredRole>
                    }/>
                    <Route path="list" element={
                        <RequiredRole allowed={['advertiser']}>
                            <CampaignListPage type="my-campaigns" title="My campaigns"/>
                        </RequiredRole>
                    }/>
                    <Route path="browse" element={
                        <CampaignListPage type="browse-campaigns" title="Browse campaigns"/>
                    }/>
                    <Route path=":id" element={<CampaignDetailPage/>}/>
                    <Route path=":id/promoters" element={
                        <RequiredRole allowed={['advertiser']}>
                            <CampaignPromotersPage />
                        </RequiredRole>
                    }/>
                    <Route path=":id/edit" element={
                        <RequiredRole allowed={['advertiser']}>
                            <UpdateCampaignPage/>
                        </RequiredRole>
                    }/>
                    <Route path=":id/edit-active" element={
                        <RequiredRole allowed={['advertiser']}>
                            <UpdateActiveCampaignPage/>
                        </RequiredRole>
                    }/>
                </Route>
                
                <Route path="campaign-links">
                    <Route path="list" element={
                        <RequiredRole allowed={['promoter', 'partner']}>
                            <CampaignLinkListPage type="my-campaign-links" title="My campaign links"/>
                        </RequiredRole>
                    }/>
                    <Route path=":id" element={<CampaignLinkDetailPage/>}/>
                </Route>

                <Route path="campaign-requests">
                    <Route
                        path=""
                        element={
                            <RequiredRole allowed={['advertiser']}>
                                <CampaignLinkRequestsPage />
                            </RequiredRole>
                        }
                    />
                    <Route
                        path=":id"
                        element={
                            <RequiredRole allowed={['advertiser']}>
                                <CampaignLinkRequestDetailPage />
                            </RequiredRole>
                        }
                    />
                </Route>

                <Route path="campaign-requests/:id/approve" element={<CampaignRequestApprovePage />} />
                <Route path="campaign-requests/:id/reject" element={<CampaignRequestRejectPage />} />

                <Route path="wallet">
                    <Route path="" element={<WalletPage/>}/>
                    <Route path="transactions" element={<TransactionsPage/>}/>
                </Route>

                <Route path="feed">
                    <Route path="best-advertisers" element={<BestAdvertisersPage/>}/>
                    <Route path="best-promoters" element={<BestPromotersPage/>}/>
                    <Route path="best-partners" element={<BestPartnersPage/>}/>
                </Route>

                <Route path="advertisers">
                    <Route path=":id" element={<AdvertiserPage/>}/>
                    <Route path=":id/campaigns"
                           element={<CampaignListPage type="advertiser-campaigns" title="Advertiser campaigns"/>}/>
                </Route>

                <Route path="promoters">
                    <Route path=":id" element={<PromoterPage/>}/>
                    <Route path=":id/:campaignLinkId" element={<PromoterPage/>}/>
                    <Route path=":id/campaign-links" element={
                        <CampaignLinkListPage type="promoter-campaign-links" title="Promoter campaign links"/>
                    }/>
                </Route>

                <Route path="partners">
                    <Route path=":id" element={<PartnerPage/>}/>
                    <Route path=":id/campaign-links" element={
                        <CampaignLinkListPage type="partner-campaign-links" title="Partner campaign links"/>
                    }/>
                </Route>

                <Route path="invite-users" element={
                    <RequiredRole allowed={['partner']}>
                        <InviteUsersPage/>
                    </RequiredRole>
                }/>

                {/* Fallback within “app” */}
                <Route path="*" element={<Navigate to="home" replace/>}/>
            </Route>
        </Routes>
    </BrowserRouter>
);

export default AppRouter;
